import React from 'react';

/**
 * React.lazy that survives a stale cache after a deploy.
 *
 * When the frontend is rebuilt, code-split chunk filenames get new content
 * hashes. A browser still holding the previous index.html requests the OLD
 * chunk name, which no longer exists -> import() rejects with a ChunkLoadError.
 * With no error boundary that rejection unmounts the whole app => white screen.
 *
 * This wrapper catches that one case and force-reloads the page ONCE (guarded by
 * sessionStorage so we never loop) so the browser fetches the fresh index.html
 * and the current chunks. Any other error is rethrown for a real error boundary.
 */
export default function lazyWithReload(factory) {
  return React.lazy(() =>
    factory().catch((err) => {
      const isChunkError =
        err && (err.name === 'ChunkLoadError' ||
          /Loading chunk [\d]+ failed/i.test(err.message || '') ||
          /Failed to fetch dynamically imported module/i.test(err.message || ''));
      if (isChunkError) {
        const KEY = 'app_chunk_reloaded_at';
        const last = Number(sessionStorage.getItem(KEY) || 0);
        // Only reload if we haven't just done so (avoid an infinite loop when the
        // chunk is genuinely gone rather than merely stale).
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          // Return a never-resolving module so nothing renders before reload.
          return new Promise(() => {});
        }
      }
      throw err;
    })
  );
}
